import React from 'react';
import {
    Box,
    Typography,
    Paper,
    Avatar,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider
} from '@mui/material';
import {
    PersonOutlineOutlinedIcon,
    EmailOutlinedIcon,
    CallIcon,
    LanguageIcon,
    LinkedInIcon,
    WorkIcon,
    LocationOnIcon,
    BusinessIcon
} from 'icons/materialIcons';

const TeamV2MemberPreview = ({ memberData, isNewMember = false }) => {
    const getContactLinkIcon = (type) => {
        const iconMap = {
            email: <EmailOutlinedIcon />,
            phone: <CallIcon />,
            website: <LanguageIcon />,
            linkedin: <LinkedInIcon />
        };
        return iconMap[type] || <PersonOutlineOutlinedIcon />;
    };

    const getContactLinkColor = (type) => {
        const colorMap = {
            email: '#EA4335',
            phone: '#34A853',
            website: '#4285F4',
            linkedin: '#0077B5'
        };
        return colorMap[type] || '#666666';
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'success';
            case 'invited': return 'warning';
            case 'suspended': return 'error';
            default: return 'default';
        }
    };

    return (
        <Box sx={{ height: '100%', overflow: 'auto' }}>
            <Paper sx={{
                padding: '1.5rem',
                height: 'fit-content',
                minHeight: '100%',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
                {/* Header */}
                <Box sx={{ textAlign: 'center', marginBottom: '2rem' }}>
                    <Avatar
                        src={memberData.profilePictureUrl}
                        sx={{
                            width: 100,
                            height: 100,
                            margin: '0 auto 1rem',
                            backgroundColor: '#f0f0f0'
                        }}
                    >
                        <PersonOutlineOutlinedIcon sx={{ fontSize: 50, color: '#999' }} />
                    </Avatar>

                    <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: 0.5 }}>
                        {memberData.firstName && memberData.lastName
                            ? `${memberData.firstName} ${memberData.lastName}`
                            : 'New Team Member'
                        }
                    </Typography>

                    {memberData.jobTitle && (
                        <Typography variant="body1" sx={{ color: '#666', marginBottom: 0.5 }}>
                            {memberData.jobTitle}
                        </Typography>
                    )}

                    {memberData.company && (
                        <Typography variant="body2" sx={{ color: '#999' }}>
                            {memberData.company}
                        </Typography>
                    )}

                    {/* Status Chip */}
                    <Box sx={{ marginTop: 1 }}>
                        <Chip
                            label={memberData.status?.charAt(0).toUpperCase() + memberData.status?.slice(1)}
                            color={getStatusColor(memberData.status)}
                            size="small"
                        />
                    </Box>
                </Box>

                {/* Bio Section */}
                {memberData.bio && (
                    <Box sx={{ marginBottom: '2rem' }}>
                        <Typography variant="body2" sx={{
                            color: '#666',
                            lineHeight: 1.6,
                            textAlign: 'center',
                            fontStyle: 'italic'
                        }}>
                            "{memberData.bio}"
                        </Typography>
                    </Box>
                )}

                {/* Contact Information */}
                <Box sx={{ marginBottom: '2rem' }}>
                    <Typography variant="h6" sx={{
                        fontWeight: 600,
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <EmailOutlinedIcon />
                        Contact Information
                    </Typography>

                    <List sx={{ '& .MuiListItem-root': { padding: '4px 0' } }}>
                        {memberData.email && (
                            <ListItem sx={{ padding: '8px 0' }}>
                                <ListItemIcon sx={{ minWidth: '32px', color: '#EA4335' }}>
                                    <EmailOutlinedIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary={memberData.email}
                                    sx={{ '& .MuiTypography-root': { fontSize: '0.9rem' } }}
                                />
                            </ListItem>
                        )}

                        {memberData.phone && (
                            <ListItem sx={{ padding: '8px 0' }}>
                                <ListItemIcon sx={{ minWidth: '32px', color: '#34A853' }}>
                                    <CallIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary={memberData.phone}
                                    sx={{ '& .MuiTypography-root': { fontSize: '0.9rem' } }}
                                />
                            </ListItem>
                        )}

                        {memberData.location && (
                            <ListItem sx={{ padding: '8px 0' }}>
                                <ListItemIcon sx={{ minWidth: '32px', color: '#FF5722' }}>
                                    <LocationOnIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary={memberData.location}
                                    sx={{ '& .MuiTypography-root': { fontSize: '0.9rem' } }}
                                />
                            </ListItem>
                        )}
                    </List>
                </Box>

                {/* Contact Links */}
                {memberData.contactLinks && memberData.contactLinks.length > 0 && (
                    <Box sx={{ marginBottom: '2rem' }}>
                        <Typography variant="h6" sx={{
                            fontWeight: 600,
                            marginBottom: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}>
                            <LanguageIcon />
                            Contact Links
                        </Typography>

                        <List sx={{ '& .MuiListItem-root': { padding: '4px 0' } }}>
                            {memberData.contactLinks.map((link, index) => (
                                <React.Fragment key={link.id}>
                                    <ListItem sx={{ padding: '8px 0' }}>
                                        <ListItemIcon sx={{
                                            minWidth: '32px',
                                            color: getContactLinkColor(link.type)
                                        }}>
                                            {getContactLinkIcon(link.type)}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={link.title}
                                            secondary={link.url}
                                            sx={{
                                                '& .MuiTypography-root': { fontSize: '0.9rem' },
                                                '& .MuiListItemText-secondary': { fontSize: '0.8rem' }
                                            }}
                                        />
                                    </ListItem>
                                    {index < memberData.contactLinks.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    </Box>
                )}

                {/* Work Information */}
                <Box sx={{ marginBottom: '2rem' }}>
                    <Typography variant="h6" sx={{
                        fontWeight: 600,
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <WorkIcon />
                        Work Information
                    </Typography>

                    <List sx={{ '& .MuiListItem-root': { padding: '4px 0' } }}>
                        {memberData.department && (
                            <ListItem sx={{ padding: '8px 0' }}>
                                <ListItemIcon sx={{ minWidth: '32px', color: '#666' }}>
                                    <BusinessIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary={memberData.department}
                                    secondary="Department"
                                    sx={{
                                        '& .MuiTypography-root': { fontSize: '0.9rem' },
                                        '& .MuiListItemText-secondary': { fontSize: '0.8rem' }
                                    }}
                                />
                            </ListItem>
                        )}

                        {memberData.role && (
                            <ListItem sx={{ padding: '8px 0' }}>
                                <ListItemIcon sx={{ minWidth: '32px', color: '#666' }}>
                                    <PersonOutlineOutlinedIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary={memberData.role.charAt(0).toUpperCase() + memberData.role.slice(1)}
                                    secondary="Role"
                                    sx={{
                                        '& .MuiTypography-root': { fontSize: '0.9rem' },
                                        '& .MuiListItemText-secondary': { fontSize: '0.8rem' }
                                    }}
                                />
                            </ListItem>
                        )}
                    </List>
                </Box>

                {/* Member Stats */}
                {!isNewMember && (
                    <Box>
                        <Typography variant="h6" sx={{
                            fontWeight: 600,
                            marginBottom: '1rem'
                        }}>
                            Member Activity
                        </Typography>

                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 2,
                            textAlign: 'center'
                        }}>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 600, color: '#000' }}>
                                    {memberData.totalLogins || 0}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#666' }}>
                                    Total Logins
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 600, color: '#000' }}>
                                    {memberData.isActive ? 'Yes' : 'No'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#666' }}>
                                    Active Status
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* New Member Notice */}
                {isNewMember && (
                    <Box sx={{
                        backgroundColor: '#f0f8ff',
                        padding: '1rem',
                        borderRadius: '8px',
                        textAlign: 'center',
                        marginTop: '2rem'
                    }}>
                        <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 500 }}>
                            This is a preview of how the team member will appear
                        </Typography>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default TeamV2MemberPreview;
